import * as React from 'react'
import styled from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { H1, Spacer, H2, BodyTextSpan } from '../../../../common/src/components/core'
import { appContext } from '../../../../common/src/context'
import { FlagName, setFlag, getFlag, FlagNames } from '../../../../common/src/flags'
import memoize from '../../../../common/src/memoize'
import { JobName } from '../../../../common/src/types.gen'
import JobsHistorySection from '../components/JobsHistorySection'
import { JobsRunningSection, AdminJobSummary } from './JobsRunningSection'

const idsArgumentDescription =
  'A list of ids for which to run this job. The ids are integers separated either by any of [comma, semicolon, space, tab, or newline]. Example: "345,456,567".'

const adminJobs = [
  {
    description:
      'The most popular admin task. Work is allocated to operators via `stations`. Each station contains some amount of tasks. Ideally, we want each operator to do roughly the same amount of work. Rebalancing stations allocates tasks amongst the stations such that each station has the same amount of work, and therefore each operator has the same amount of work.',
    text: 'Rebalance Stations',
    args: { name: JobName.ADMIN_BALANCE_STATIONS },
  },
  {
    description:
      'The omnibar search is powered by an index, which sometimes gets into a bad state. This button initiates a few-hours-long rebuild of the index. The redis instance name must be specified.',
    text: 'Rebuild Search Index',
    args: { name: JobName.ADMIN_REBUILD_SEARCH_INDEX },
    argumentDescription: 'Name of redis instance (currently "redis_search" is the only instance) to rebuild',
    argumentRequired: true,
  },
  {
    description:
      'Update the search index for all `Thread`s that have been updated in the specified time range (and all of their associated data such as prefix to search result mappings).  Required input: two timestamps separated by a single space.  First timestamp = `start time` (inclusive); Second timestamp = `end time` (exclusive).',
    text: 'Update Search Index for Threads',
    args: { name: JobName.ADMIN_UPDATE_SEARCH_INDEX_FOR_THREADS },
    argumentDescription: 'Two UTC timestamps (like `2020-07-23T18:24:56.591Z`) separated by a single space',
    argumentRequired: true,
  },
  {
    description:
      'Remove search results (and all of their associated data such as prefix to search result mappings) from Redis.  Input can be either a single `SearchResultKey` (of the form `TASK-12345` or `THREAD-12345`) in which case that single result will be removed, OR two thread ids separated by a space (e.g. `1` `99999`) in which case the search results for all threads in that range (inclusive) will be removed.  If a thread (or search result) does not exist for a given id, there is no effect (i.e. this is fine).  Note that a range of `Task`s cannot be removed.',
    text: 'Remove Search Results',
    args: { name: JobName.ADMIN_REMOVE_SEARCH_RESULT },
    argumentDescription:
      'SearchResultKey (e.g. THREAD-12345) to delete, OR two thread ids separated by a space (e.g. 1 999999)',
    argumentRequired: true,
  },
  {
    text: 'Buy Missing Endpoints',
    description:
      'Buys missing endpoints for a set of tasks. They must be in one firm. Buys endpoints according to the sources specified in the firm config.',
    args: { name: JobName.ADMIN_BUY_MISSING_ENDPOINTS },
    argumentDescription: idsArgumentDescription,
    argumentRequired: true,
  },
  {
    description:
      'Logs out all users. Use for temporary relief if CC is overloaded, or if for any reason you need to quickly pause all operator activity.',
    text: 'Logout All Users',
    args: { name: JobName.ADMIN_LOGOUT_ALL_USERS },
  },
  {
    description:
      '- Removes `IN_PERSON` as a TourType for the given **Property Tasks**.\n- Rejects all future `IN_PERSON` showings at the listed task IDs\n- Idempotent: safe to run multiple times.',
    text: 'COVID lockdown: cancel in-person tours at task IDs',
    args: { name: JobName.ADMIN_COVID_LOCKDOWN_FOR_TASK_IDS },
    argumentDescription: idsArgumentDescription,
    argumentRequired: true,
  },
  {
    description:
      "- Scans all policy language across the firm and alerts the `OPS` `BOSS` if policy could conflict with the tour type.\n- Idempotent: it won't msg the `OPS` `BOSS` more than once.",
    text: 'Warn `OPS` `BOSS` of `TourType` + policy conflicts',
    args: { name: JobName.ADMIN_WARN_BOSS_TOUR_TYPE_POLICY_CONFLICTS },
    argumentDescription: idsArgumentDescription,
    argumentRequired: true,
  },
  {
    description:
      "Creates cases in the Appfolio SalesForce for endpoints that haven't been used in a while. Kate's team uses these cases to reach out to our clients to tell them to switch their marketing.",
    text: 'Marketing Watchdog',
    args: { name: JobName.ADMIN_MARKETING_WATCHDOG },
  },
  {
    description:
      "Creates Friday Funnel reports for ALL active leasing firms. Each firm's report is created asynchronously in the background via lambda. Use this if the Friday Funnel creation job failed and reports need to be regenerated for all firms. Takes ~1 minute per firm.",
    text: 'Create All Friday Funnel Reports',
    args: { name: JobName.ADMIN_CREATE_ALL_FUNNEL_REPORTS },
  },
  {
    description:
      "Sends Friday Funnel emails to ALL eligible agents and bosses at ALL active leasing firms. This bypasses the normal Friday noon-1pm timing constraint, allowing emergency recovery. IMPORTANT: Run 'Create All Friday Funnel Reports' first to ensure reports exist in S3. Each firm's emails are sent asynchronously via lambda.",
    text: 'Send All Friday Funnel Emails',
    args: { name: JobName.ADMIN_SEND_ALL_FUNNEL_EMAILS },
  },
  {
    description:
      "**IRREVERSIBLE.** Rotates the CSV API key of EVERY active LEASING firm (hard cutover: old keys stop working immediately, no grace window) and re-sends each firm's api-docs email with the new key. Excel data connections using old keys break until customers re-paste a URL from that email — coordinate the CS pre-announcement before running for real. **ALWAYS run with input `dryRun` first** and review the reported counts; then clear the input and run for real. If some firms fail, rerun just those via the per-firm Rotate API Key button on the firm page (do NOT re-run this job to mop up — it re-rotates and re-emails everyone).",
    text: 'Rotate All Firm API Keys',
    args: { name: JobName.ADMIN_ROTATE_ALL_FIRM_API_KEYS },
    argumentDescription:
      'Type "dryRun" to only report what would rotate/email. Leave empty to actually rotate every LEASING firm.',
  },
  {
    description:
      'Simulate the receipt of any inbound Mailgun Email.  The "email" must be in json format and include at minimum the following fields:  `sender`, `recipient`, `From`, `To`, `Subject`, `Message-Id`, and `body-plain`.',
    text: 'Receive Email',
    args: { name: JobName.ADMIN_RECEIVE_EMAIL },
    argumentDescription: 'raw email (in json format)',
    argumentRequired: true,
  },
  {
    description:
      'Simulate the receipt of an inbound ILS Email. You can only send to `PROSPECT` threads whose leasing task is a property task.',
    text: 'Receive ILS Email',
    args: { name: JobName.ADMIN_RECEIVE_ILS_EMAIL },
    argumentDescription: 'thread id on which to receive the ILS email',
    argumentRequired: true,
  },
  {
    description:
      'Provisions a new SMS number for the given Firm Task, associates it with the task and existing agent threads, and messages agents on existing threads to inform them of the new number.',
    text: 'Rotate Firm Task Number',
    args: { name: JobName.ADMIN_ROTATE_FIRM_TASK_NUMBER },
    argumentDescription: 'ID of firm task',
    argumentRequired: true,
  },
  {
    description: 'Creates and cancels a showing on each task in minnix.',
    text: 'Test mass cancellation',
    args: { name: JobName.ADMIN_TEST_MASS_CANCELLATIONS },
    argumentDescription: 'firm id and number of tasks with showings to cancel separated by a comma',
    argumentRequired: true,
  },
  {
    description: 'Reruns actions based on their id in actionHistory',
    text: 'Rerun bot actions',
    args: { name: JobName.ADMIN_RERUN_FAILED_BOTACTIONS },
    argumentDescription: 'ids of actionHistory entries separated by spaces',
    argumentRequired: true,
  },
  {
    description: 'Forecast next week shift slot count using Msg Volume Prediction model',
    text: 'Forecast next week shift',
    args: { name: JobName.ADMIN_FORECAST_NEXT_WEEK_SHIFT },
    argumentDescription: 'start date like `2021-04-20`',
    argumentRequired: true,
  },
  {
    description: 'Start retraining for Msg Volume Prediction model',
    text: 'Retrain Msg Volume Prediction Model',
    args: { name: JobName.ADMIN_MSG_VOLUME_TRAINING },
  },
  {
    description: 'Start retraining for Galaxy model',
    text: 'Retrain Galaxy Model',
    args: { name: JobName.ADMIN_GALAXY_TRAINING },
  },
  {
    description: 'Start retraining for Showing Negotiator model',
    text: 'Retrain Showing Negotiator Model',
    args: { name: JobName.ADMIN_SHOWING_NEGOTIATOR_TRAINING },
  },
  {
    description: 'Start retraining for ILS Parser model',
    text: 'Retrain ILS Parser Model',
    args: { name: JobName.ADMIN_ILS_PARSER_TRAINING },
  },
  {
    description: 'Start retraining for Recommedation model',
    text: 'Retrain REC Model',
    args: { name: JobName.ADMIN_REC_TRAINING },
  },
  {
    description:
      'Ingests Properties Data from Onboarding Spreadsheets for existing Firms and Properties. Updates Firms & Leasing Properties. Document share permissions must be at least set to `Anyone on the internet with this link can view`.',
    text: 'Ingest Properties Onboarding Spreadsheet',
    args: { name: JobName.ADMIN_INGEST_ONBOARDING_SPREADSHEET_PROPERTIES },
    argumentDescription: 'Property Data Sheets URL',
    argumentRequired: true,
  },
  {
    description:
      'Ingests Agents Data from Onboarding Spreadsheets for existing Firms and Properties. Creates Contacts, Agent Tasks, and Threads. Document share permissions must be at least set to `Anyone on the internet with this link can view`.',
    text: 'Ingest Agents Onboarding Spreadsheet',
    args: { name: JobName.ADMIN_INGEST_ONBOARDING_SPREADSHEET_AGENTS },
    argumentDescription: 'Agent Data Sheets URL',
    argumentRequired: true,
  },
  {
    text: 'Backfill Guest Cards for All Firms',
    description: 'Retries writing last n days worth of failed guest card writes',
    args: { name: JobName.ADMIN_BACKFILL_GUEST_CARDS_FOR_ALL_FIRMS },
    argumentDescription: 'a number like 3',
    argumentRequired: true,
  },
  {
    text: 'Generic Admin Job',
    description: 'Runs a specified function',
    args: { name: JobName.ADMIN_GENERIC },
    argumentDescription: 'name of the function in AdminService.java to run',
    argumentRequired: true,
  },
  {
    text: 'Set Apm Info',
    description: "Updates each tasks' pmItemKeys, then syncs lisa info and agent availability to the firm's vhost",
    args: { name: JobName.ADMIN_SET_APM_INFO },
    argumentDescription:
      'Json where key is the CC task id and value is the apm id, e.g. Desert Meadows would have the input { "7851": "31" }',
    argumentRequired: true,
  },
  {
    text: 'Migrate tasks to new PM',
    description:
      "Updates each tasks' pmItemKeys based on a copy of https://docs.google.com/spreadsheets/d/1mIPebUy_bVjRRi8KImGNBmUto2yHmG5qFWPRAkNJPAE",
    args: { name: JobName.ADMIN_SET_PM_INFO },
    argumentDescription: 'csv imported from spreadsheet',
    argumentRequired: true,
  },
  {
    text: 'Resend failed messages',
    description: 'Tries to resend all the failed-to-send messages of the specified type since the specified time.',
    args: { name: JobName.ADMIN_RESEND_FAILED_MESSAGES },
    argumentDescription:
      'Timestamp for when to start the search for failed messages (e.g. 2021-12-10T20:20:00Z) and the message type (SMS/EMAIL), separated by a space',
    argumentRequired: true,
  },
  {
    text: 'Create next week shifts',
    description: 'Crate or update next week shifts.',
    args: { name: JobName.ADMIN_CREATE_NEXT_WEEK_SHIFTS },
  },
  {
    text: 'Post one week forecast to Slack',
    description: 'Send one week forecast to #lisa-ops.',
    args: { name: JobName.ADMIN_POST_ONE_WEEK_SHIFT_FORECAST_TO_SLACK },
  },
  {
    text: 'Send one month forecast to af event steam',
    description: 'Send one month forecast to lisa_shift_evaluation table',
    args: { name: JobName.ADMIN_SEND_ONE_MONTH_SHIFT_FORECAST_TO_AF_EVENT_STREAM },
  },
  {
    text: 'Add shift',
    description: 'Add a new shift for today',
    args: { name: JobName.ADMIN_ADD_SHIFT },
    argumentRequired: true,
    argumentDescription:
      '(start hour in Pacific),(end hour in Pacific),(number of slots) e.g. 14,16,2 will create a 2pm-4pm shift today with 2 slots',
  },
  {
    text: 'Add phone tree routing mappings',
    description:
      "Adds csv-formatted task ID source and phone number pairs to EntRouter table. A general guideline would be to use this job when there are specific phone numbers for property tasks. But if we want e.g. all maintenance calls for a firm to go to a single number then we'd have to update ent-voice-receive.py.",
    args: { name: JobName.ADMIN_ADD_ENT_ROUTER_ITEMS },
    argumentDescription: 'task ID source and phone number in csv format, e.g. "1775.MAINTENANCE, 8188898123"',
    argumentRequired: true,
  },
  {
    text: 'Update contact phone number',
    description: 'Updates the phone number for a contact',
    args: { name: JobName.ADMIN_UPDATE_CONTACT_PHONE_NUMBER },
    argumentDescription:
      "The contact's thread ID and the new phone number, separated by a space. If you'd need to delete a contact's phone number, use \"null\" as the second argument; you can only delete a contact's phone number if they don't have an existing SMS thread. In case they do, set their phone number to some \"fake\" value instead of null.",
    argumentRequired: true,
  },
  {
    text: 'Bulk update contact phone number',
    description: 'Updates the phone number for a contact',
    args: { name: JobName.ADMIN_BULK_UPDATE_CONTACT_PHONE_NUMBER },
    argumentDescription:
      'CSV-formatted list of the contactID, taskID and new phone number. TaskID is referring to the Leasing Task associated with the Thread you want to update.',
    argumentRequired: true,
  },
  {
    text: 'Get task phone trees',
    description: 'Gets the phone trees for the specified tasks',
    args: { name: JobName.ADMIN_GET_TASKS_PHONE_TREES },
    argumentDescription: 'The task IDs separated by a space',
    argumentRequired: true,
  },
  {
    text: 'Delete duplicate calEvent',
    description: 'Deletes duplicate rows from calEvent',
    args: { name: JobName.ADMIN_DELETE_DUPE_CAL_EVENT },
    argumentDescription:
      'The showing IDs that are causing a "Multiple entries with same key" SQL error, separated by a space',
    argumentRequired: true,
  },
  {
    text: 'Set thread time updated to 7 days ago',
    args: { name: JobName.ADMIN_SET_THREAD_TIME_UPDATED_TO_7_DAYS_OLD },
    argumentDescription: 'Comma separated list of thread ids.',
    argumentRequired: true,
  },
  {
    text: 'Migrate firm vhost',
    args: { name: JobName.ADMIN_MIGRATE_VHOST },
    argumentDescription: 'The existing and the desired vhosts, separated by a space',
    argumentRequired: true,
  },
  {
    text: 'Migrate PM item keys',
    args: { name: JobName.ADMIN_MIGRATE_PM_ITEM_KEYS },
    argumentDescription: 'Number of the loop to start the migration from',
    argumentRequired: false,
  },
  {
    text: 'Generate audio url',
    args: { name: JobName.ADMIN_GENERATE_AUDIO_URL },
    argumentDescription: 'Task ID and audio style (greeting/voicemail) separated by a space',
    argumentRequired: true,
  },
  {
    text: 'Fix Property Twilio Webhooks',
    description:
      'Sets the Twilio SmsUrl/VoiceUrl/VoiceFallbackUrl on property-task phone numbers to the same values normal provisioning would have set. Use this to backfill numbers that were purchased by hand directly in Twilio (with their `endpoint` row added manually) instead of through the normal task provisioning flow. Every number must already have a saved `Endpoint` with a `twilioSid`, belonging to a LEASING_PROPERTY task of the given firm. Refuses to run for any firm with the USE_PHONE_MANAGER_FOR_VOICE flag enabled, since this job does not register numbers with Phone Manager.',
    args: { name: JobName.ADMIN_FIX_PROPERTY_TWILIO_WEBHOOKS },
    argumentDescription:
      'Firm id followed by one or more phone numbers, separated by comma/space/newline, e.g. "92,6501234567,6501234568"',
    argumentRequired: true,
  },
  {
    text: 'Migrate Twilio Numbers between Message Services',
    description:
      'Moves twilio phone numbers and task endpoints from one Message Service to another. "sourceTwilioMessageServiceSid" is the sid of the message service to move numbers FROM, while "targetTwilioMessageServiceSid" is the sid of the message service to move the numbers TO. If you want to also update the capacity of the target message service, then you can add "targetTwilioMessageServiceCapacity"',
    args: { name: JobName.ADMIN_MIGRATE_TWILIO_NUMBERS },
    argumentDescription:
      'Json-formatted payload e.g. {"sourceTwilioMessageServiceSid": "...", "targetTwilioMessageServiceSid": "...", "targetTwilioMessageServiceCapacity": 1000}',
    argumentRequired: true,
  },
  {
    text: 'Twilio Migration: Migrate Phone Number Config to APM',
    description:
      "**Step 3** of the Lisa to Leasing Performer phone migration. Run it after Twilio has moved the numbers. Step 1 runs from the firm's admin page. Registers each active property phone number as a lead routing number in the property app and releases it in Lisa. Safe to re-run: released numbers are not sent again. Migrated numbers forward to the property's forwarding number until Leasing Performer is enabled. The result is in the Raw link below. To migrate one property, add its Lisa property task id as a third field. The id is on the property task's page. During the pilot, always add the task id; without it the job migrates the whole vhost. Pilot: for each pilot property, run Step 1 then Step 3 with its task id. Finish all pilot Step 3 runs before you run Step 1 without a task id, because Step 1 lists only unreleased numbers. Then run both steps without a task id.",
    args: { name: JobName.ADMIN_PHONE_CONFIG_MIGRATION },
    argumentDescription:
      'firm task id,vhost, e.g. 12345,acme.qa. For one property add the property task id: 12345,acme.qa,45678',
    argumentRequired: true,
  },
  {
    text: 'Refresh Thread PmItems',
    description:
      'Clears links between threads and the associated PM Guest Cards/Prospects. Then re-syncs the guest cards with the firms associated PM system.',
    args: { name: JobName.ADMIN_REFRESH_THREAD_PM_ITEMS },
    argumentDescription:
      'Json-formatted payload e.g. {"firmId": "92", "startDate": "2023-01-01", "endDate": "2023-03-01"}',
    argumentRequired: true,
  },
  {
    text: 'Create team',
    args: { name: JobName.ADMIN_CREATE_TEAM },
    argumentDescription: 'Team name and Slack channel ID, separated by a space',
    argumentRequired: true,
  },
] as AdminJobSummary[]

if (!appContext().isProduction) {
  adminJobs.push({
    description: 'Sets up local instances of CC so that testing the integration with APM is easier',
    text: 'Set up APM testing',
    args: { name: JobName.ADMIN_SETUP_APM_TESTING },
  })
  adminJobs.push({
    description: 'Sets the server time to the given timestamp. Only works in dev.',
    text: 'Set Time',
    args: { name: JobName.ADMIN_SET_SERVER_TIME },
    argumentDescription: 'timestamp like `2020-07-23T18:24:56.591Z`',
    argumentRequired: true,
  })
  adminJobs.push({
    text: 'Create Localhost Firm',
    description: 'Create a firm that syncs with local APM',
    args: { name: JobName.ADMIN_CREATE_LOCALHOST_FIRM },
  })
}

export default function AdminsDetail() {
  return (
    <div>
      <H1>Dynasty Admins</H1>
      <JobsRunningSection jobs={adminJobs} />
      <Spacer height={16} />
      <JobsHistorySection jobKey={JSON.stringify({ type: 'admin' })} />
      <Spacer height={32} />
      <H2>
        Local settings
        <Spacer width={8} />
        <BodyTextSpan>These are stored in your browser, in localStorage.</BodyTextSpan>
      </H2>
      <Spacer height={16} />
      <FlagsEditor />
    </div>
  )
}

/**
 * Lets the user toggle LocalStorage flags. See flags.ts
 */
class FlagsEditor extends React.PureComponent<Record<string, never>, { flags: { key: string; val: boolean }[] }> {
  state = { flags: loadFlags() }
  private handlers = {}

  render() {
    return (
      <div>
        {this.state.flags.map(flag => (
          <FlagButton
            key={flag.key}
            onClick={memoize(this.handlers, flag.key, (k: FlagName) => () => this.onToggle(k))}
            filled={flag.val}
            size="medium"
          >
            {flag.key}
          </FlagButton>
        ))}
      </div>
    )
  }

  private onToggle = (key: FlagName) => {
    setFlag(key, !getFlag(key))
    this.setState({ flags: loadFlags() })
  }
}

function loadFlags() {
  return FlagNames.map(key => ({ key, val: getFlag(key) }))
}

const FlagButton = styled(Button)`
  margin: 0 8px 8px 0;
`
